import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Award, Download, PartyPopper, User } from "lucide-react";
import { openCertificatePrintWindow } from "@/lib/certificateTemplate";
import { toast } from "sonner";
import type { Certificate } from "@/hooks/useCertificates";
import { format } from "date-fns";
import certificateBackground from "@/assets/certificate-background.jpg";
import eabLogoHorizontal from "@/assets/eab-logo-horizontal.png";

interface CurriculumCompleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  certificate: Certificate | null;
}

export function CurriculumCompleteDialog({
  open,
  onOpenChange,
  certificate,
}: CurriculumCompleteDialogProps) {
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (open) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleDownload = () => {
    if (!certificate) return;

    const opened = openCertificatePrintWindow({
      userName: certificate.user_name,
      courseName: certificate.curriculum_title,
      certificateNumber: certificate.certificate_number,
      issuedAt: certificate.issued_at,
    });

    if (!opened) {
      toast.error("Your browser blocked the certificate window. Please allow pop-ups and try again.");
    }
  };

  const issueDate = certificate ? format(new Date(certificate.issued_at), "MMMM d, yyyy") : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl text-center overflow-y-auto max-h-[90vh]">
        {/* Google Fonts for Tangerine */}
        <link href="https://fonts.googleapis.com/css2?family=Tangerine:wght@700&display=swap" rel="stylesheet" />
        
        <div className="relative">
          {showConfetti && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
              <div className="animate-bounce">
                <PartyPopper className="h-16 w-16 text-yellow-500" />
              </div>
            </div>
          )}
        </div>
        <DialogHeader className="text-center sm:text-center pt-2">
          <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/60 shadow-lg">
            <Award className="h-8 w-8 text-primary-foreground" />
          </div>
          <DialogTitle className="text-2xl">
            Congratulations! 🎉
          </DialogTitle>
          <DialogDescription className="text-base space-y-1 pt-1">
            <p>
              You have successfully completed the curriculum
            </p>
            <p className="font-semibold text-foreground text-lg">
              {certificate?.curriculum_title}
            </p>
          </DialogDescription>
        </DialogHeader>

        {/* Certificate Preview */}
        {certificate && (
          <div className="my-4">
            <div 
              className="mx-auto relative overflow-hidden rounded-lg shadow-xl"
              style={{ 
                width: "100%",
                maxWidth: "550px",
                aspectRatio: "1100/780",
                backgroundImage: `url(${certificateBackground})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              {/* Inner White Box */}
              <div 
                className="absolute flex flex-col items-center text-center"
                style={{
                  top: "3.85%",
                  left: "3.64%",
                  right: "3.64%",
                  bottom: "3.85%",
                  background: "rgba(255, 255, 255, 0.92)",
                  padding: "3% 3.64% 2.56%",
                  justifyContent: "flex-start",
                  paddingTop: "3%",
                }}
              >
                {/* Logo Section */}
                <div style={{ marginBottom: "6%", width: "60%" }}>
                  <img 
                    src={eabLogoHorizontal} 
                    alt="Entrepreneurs Across Borders" 
                    style={{ width: "100%", height: "auto" }}
                  />
                </div>
                
                {/* Certificate Title */}
                <h1 
                  className="text-lg md:text-xl font-normal uppercase"
                  style={{ 
                    color: "#1C61E0",
                    letterSpacing: "0.15em",
                    marginBottom: "2px",
                  }}
                >
                  Certificate
                </h1>
                
                {/* Of Completion */}
                <div 
                  className="flex items-center justify-center gap-1 text-[8px] uppercase"
                  style={{
                    color: "#666",
                    letterSpacing: "1px",
                    marginBottom: "4%",
                  }}
                >
                  <span 
                    className="w-6 h-0.5"
                    style={{ background: "linear-gradient(90deg, #1C61E0, #8FCA3F)" }}
                  />
                  of Completion
                  <span 
                    className="w-6 h-0.5"
                    style={{ background: "linear-gradient(90deg, #8FCA3F, #1C61E0)" }}
                  />
                </div>
                
                {/* Presented Text */}
                <p 
                  className="text-[7px] uppercase"
                  style={{
                    color: "#555",
                    letterSpacing: "2px",
                    marginBottom: "3%",
                  }}
                >
                  We Proudly Present This Certificate To
                </p>
                
                {/* Recipient Name */}
                <h2 
                  className="text-2xl md:text-3xl font-bold"
                  style={{ 
                    fontFamily: "'Tangerine', cursive",
                    color: "#1C61E0",
                    marginBottom: "2%",
                    lineHeight: 1,
                  }}
                >
                  {certificate.user_name}
                </h2>
                
                {/* Course Text */}
                <p 
                  className="text-[8px]"
                  style={{ color: "#555", marginBottom: "4%" }}
                >
                  for completing <span style={{ fontWeight: 600, color: "#333" }}>{certificate.curriculum_title}</span>
                </p>
                
                {/* Details Section */}
                <div className="text-center space-y-0.5">
                  <div 
                    className="text-[6px] uppercase"
                    style={{ color: "#555", letterSpacing: "0.5px" }}
                  >
                    Issued: <span style={{ fontWeight: 600 }}>{issueDate}</span>
                  </div>
                  <div 
                    className="text-[6px] uppercase"
                    style={{ color: "#555", letterSpacing: "0.5px" }}
                  >
                    ID: <span style={{ fontWeight: 600 }}>{certificate.certificate_number}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
          <User className="h-4 w-4" />
          This certificate has been saved to your profile for future access.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button onClick={handleDownload} className="flex-1">
            <Download className="h-4 w-4 mr-2" />
            Download Certificate
          </Button>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1"
          >
            Continue Learning
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
